import type { Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ALPHA, EVENT_LIST, FONT_SIZE, LINE_HEIGHT, NEUTRAL, VERSION_HISTORY } from "../../../theme/tokens";

/** The height of a version's title line: the marker is centred on it, and the rail runs from marker to marker. */
const TITLE_LINE = `calc(${FONT_SIZE.SECONDARY} * ${LINE_HEIGHT.TIGHT})`;
const MARKER_CENTER = `calc(${TITLE_LINE} / 2)`;
/** A row's vertical padding, in spacing units. The rail reaches through it, so the rows join into one line. */
const ROW_PY = 1;

/**
 * A version row of `VersionHistoryMenu`. The rail is drawn per row, edge to edge through its padding, so adjacent rows
 * join into one continuous line — starting at the first marker and ending at the last, never dangling past either.
 */
export const versionRowSx =
	({ isFirst, isLast }: { isFirst: boolean; isLast: boolean }) =>
	(theme: Theme) => {
		const rowPad = theme.spacing(ROW_PY);
		const brand = theme.palette.primary.main;
		return {
			"--version-title-line": TITLE_LINE,
			alignItems: "flex-start",
			gap: 1.25,
			mx: 0.5,
			px: 1.25,
			py: ROW_PY,
			minHeight: 0,
			borderRadius: 1,
			whiteSpace: "normal",
			"& .version-marker": {
				position: "relative",
				alignSelf: "stretch",
				flexShrink: 0,
				width: VERSION_HISTORY.MARKER_PX,
				pt: `calc(${MARKER_CENTER} - ${VERSION_HISTORY.MARKER_PX / 2}px)`,
				"& > *": { position: "relative", zIndex: 1 },
				"&::before": {
					content: '""',
					display: isFirst && isLast ? "none" : "block",
					position: "absolute",
					insetInlineStart: `calc(50% - ${EVENT_LIST.CONNECTOR_PX / 2}px)`,
					width: `${EVENT_LIST.CONNECTOR_PX}px`,
					top: isFirst ? MARKER_CENTER : `-${rowPad}`,
					bottom: isLast ? `calc(100% - ${MARKER_CENTER})` : `-${rowPad}`,
					backgroundColor: NEUTRAL[200],
				},
			},
			"&:hover, &.Mui-focusVisible": { backgroundColor: alpha(brand, ALPHA.HOVER_OVERLAY) },
			"&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": { backgroundColor: alpha(brand, ALPHA.SELECTED_OVERLAY) },
		};
	};
